import {DidomiSDK} from "@didomi/react";
import {Global, ThemeProvider, withEmotionCache} from "@emotion/react";
// import {captureRemixErrorBoundaryError, withSentry} from "@sentry/remix";
import "@fontsource/arvo/400-italic.css";
import "@fontsource/arvo/400.css";
// import "@fontsource/arvo/700-italic.css";
import "@fontsource/arvo/700.css";
import "@fontsource/arvo/700-italic.css";
import "@fontsource/manrope/400.css";
// import "@fontsource/manrope/500.css";
import "@fontsource/manrope/600.css";
import "@fontsource/manrope/700.css";
import {json} from "@remix-run/node";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  isRouteErrorResponse,
  useLoaderData,
  useLocation,
  useNavigation,
  useRouteError,
  useRouteLoaderData,
} from "@remix-run/react";
import {useContext, useEffect, useRef, useState} from "react";
import {fetcher} from "./api/fetcher.js";
import ExternalScripts from "./components/ExternalScripts.jsx";
import {FontSizeProvider, useFontSize} from "./context/fontSizeChange.jsx";
import {PathHistoryProvider} from "./context/pathHistoryContext.jsx";
import useHasHydrated from "./hooks/useHasHydrated.js";
import {useGetMediaBreakpoints} from "./hooks/useMediaQuery.js";
import {Page404} from "./layouts/NotFoundPage";
import ClientStyleContext from "./styles/client.context";
import {globalStyles} from "./styles/global.js";
import "./styles/index.css";
import {LoadingPage} from "./layouts/Loading/index.jsx";
import {newsLetterInaction, preprodAvailability} from "./session.js";
import ServerStyleContext from "./styles/server.context";
import {theme} from "./styles/theme.js";
import {validateRecaptcha} from "./utils/recaptcha.js";
import {ERROR, RECAPTCHA_ERROR, SUCCESS, validate} from "./utils/validation.js";

const PREPROD_DOMAIN = "mag.beta.viamichelin";
export async function loader({
  request,
  context: {
    language,
    basename,
    menu,
    footer,
    footerData,
    script,
    RECAPTCHA_SITE,
    domain,
    secondFooterData,
    translation,
  },
}) {
  const {
    NODE_ENV,
    VITE_ACCESS_TOKEN: ACCESS_TOKEN,
    API_BASE_URL: baseApiUrl,
  } = process?.env || {};

  const url = new URL(request.url);
  const isB16 = url.searchParams.get("b16");
  const accessToken = url.searchParams.get("accessToken");
  const cookieHeader = request.headers.get("Cookie");
  const newsLetterInactionValue = await newsLetterInaction.parse(cookieHeader);
  const isPreprod = domain.includes(PREPROD_DOMAIN);
  let renderPreprod = !isPreprod;
  const headers = new Headers();
  if (isPreprod) {
    const preprodAvailabilityValue = await preprodAvailability.parse(cookieHeader);
    if (accessToken === ACCESS_TOKEN) {
      const cookie = await preprodAvailability.serialize(accessToken);
      headers.append("Set-Cookie", cookie);
      renderPreprod = true;
    }
    if (preprodAvailabilityValue === ACCESS_TOKEN) {
      renderPreprod = true;
    }
  }

  // if (!GTM_TRACKING_ID) {
  //   console.log("Error: GTM_TRACKING_ID is not defined.");
  // }
  return json(
    {
      NODE_ENV,
      language,
      basename,
      ACCESS_TOKEN,
      scrollPageToTop: !!isB16,
      translation,
      RECAPTCHA_SITE,
      menu: menu || null,
      newsLetterInactionValue,
      footer: footer || null,
      footerData: footerData || null,
      secondFooterData: secondFooterData || null,
      script,
      baseApiUrl,
      renderPreprod,
      isPreprod,
    },
    {headers},
  );
}
export async function action({request, context}) {
  const formData = await request.formData();
  const {_action, ...formValues} = Object.fromEntries(formData);

  const {translation, RECAPTCHA_SECRET, language} = context;
  if (_action === "newsLetter") {
    const API_BASE_URL = process.env.API_BASE_URL;
    const validationRes = await validate(formValues, translation);
    if (validationRes.status === ERROR) {
      return validationRes;
    }
    const validationRecaptchaResponse = await validateRecaptcha({
      recaptchaSecret: RECAPTCHA_SECRET,
      recaptchaValue: formValues.recaptchaValue,
    });
    if (!validationRecaptchaResponse) {
      return {status: RECAPTCHA_ERROR, error: translation.nlRequiredError};
    }
    try {
      const {data} = validationRes;
      const {email, lastName, firstName} = data;
      const res = await fetcher(
        API_BASE_URL,
        `/wp-json/wp/v2/submit_nl_form?lang=${language}&email=${email}&firstName=${firstName}&lastName=${lastName}`,
      );
      if (res?.statusCode !== 200) {
        return {status: ERROR};
      }
      return {status: SUCCESS};
    } catch (err) {
      return {status: ERROR, errors: err};
    }
  }

  if (_action === "newsLetterInaction") {
    const newsLetterInactionValue = await newsLetterInaction.serialize(true);
    const headers = new Headers();
    headers.append("Set-Cookie", newsLetterInactionValue);
    return json(null, {
      headers,
    });
  }
}

export const meta = () => [{title: "Le Magazine ViaMichelin"}];

export const links = () => ({
  rel: "icon",
  href: "/magazine/icon.png",
  type: "image/png",
});

const Document = withEmotionCache(({children, title}, emotionCache) => {
  const hasHydrated = useHasHydrated();
  const {breakpoints, defaultBreakpoints} = useGetMediaBreakpoints();
  const {language, isPreprod, renderPreprod, scrollPageToTop} = useLoaderData();
  const GTM_TRACKING_ID = "GTM-KFJJNSH";
  const {fontSize} = useFontSize();
  const serverStyleData = useContext(ServerStyleContext);
  const clientStyleData = useContext(ClientStyleContext);
  const reinjectStylesRef = useRef(true);
  const location = useLocation();
  const navigation = useNavigation();

  // Only executed on client
  // When a top level ErrorBoundary or CatchBoundary are rendered,
  // the document head gets removed, so we have to create the style tags
  useEffect(() => {
    if (!reinjectStylesRef.current) {
      return;
    }
    // re-link sheet container
    emotionCache.sheet.container = document.head;

    // re-inject tags
    const tags = emotionCache.sheet.tags;
    emotionCache.sheet.flush();
    /* eslint-disable complexity/noForEach */
    tags.forEach((tag) => {
      emotionCache.sheet._insertTag(tag);
    });

    // reset cache to re-apply global styles
    clientStyleData.reset();
    // ensure we only do this once per mount
    reinjectStylesRef.current = false;
  }, [clientStyleData, emotionCache.sheet]);

  useEffect(() => {
    if (!hasHydrated) return;
    if (!scrollPageToTop && !location.hash) {
      document.body.scrollIntoView({behavior: "smooth", block: "start"});
    }
  }, [hasHydrated, location.key]);

  return (
    <html lang={language}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
        {serverStyleData?.map(({key, ids, css}) => (
          <style
            key={key}
            data-emotion={`${key} ${ids.join(" ")}`}
            /* eslint-disable security/noDangerouslySetInnerHtml */
            dangerouslySetInnerHTML={{__html: css}}
          />
        ))}
        <ExternalScripts />
      </head>
      <body>
        {/* GOOGLE TAG MANAGER NOSCRIPT */}
        {GTM_TRACKING_ID && (
          <noscript>
            <iframe
              title="GA"
              src={`https://www.googletagmanager.com/ns.html?id=${GTM_TRACKING_ID}`}
              height="0"
              width="0"
              style={{
                display: "none",
                visibility: "hidden",
              }}
            />
          </noscript>
        )}
        <Global styles={globalStyles(fontSize, hasHydrated)} />
        <ThemeProvider
          theme={{
            ...theme,
            breakpoints: hasHydrated ? breakpoints : defaultBreakpoints,
          }}
        >
          {((isPreprod && renderPreprod) || !isPreprod) && (
            <>
              <LoadingPage state={navigation.state} />
              <Outlet />
            </>
          )}
        </ThemeProvider>
        <Scripts />
        {!hasHydrated && (
          <DidomiSDK
            config={{
              app: {
                vendors: {
                  iab: {
                    all: true,
                  },
                },
              },
              languages: {
                enabled: ["de", "es", "it", "fr", "en"],
                default: language,
              },
            }}
            apiKey="a1a55c7f-7f45-4e4b-80ac-b48e44c2fe99"
            iabVersion={2}
            noticeId="bcFFynw6"
          />
        )}
      </body>
    </html>
  );
});

function App() {
  return (
    <FontSizeProvider>
      <PathHistoryProvider>
        <Document />
      </PathHistoryProvider>
    </FontSizeProvider>
  );
}

export default App;
// export default withSentry(App);

export const NotFoundPage = withEmotionCache(({children, title}, emotionCache) => {
  const hasHydrated = useHasHydrated();
  const {breakpoints, defaultBreakpoints} = useGetMediaBreakpoints();
  const {fontSize} = useFontSize();

  const serverStyleData = useContext(ServerStyleContext);
  const clientStyleData = useContext(ClientStyleContext);
  const reinjectStylesRef = useRef(true);

  useEffect(() => {
    if (!reinjectStylesRef.current) {
      return;
    }
    emotionCache.sheet.container = document.head;

    const tags = emotionCache.sheet.tags;
    emotionCache.sheet.flush();
    /* eslint-disable complexity/noForEach */
    tags.forEach((tag) => {
      emotionCache.sheet._insertTag(tag);
    });

    clientStyleData.reset();
    reinjectStylesRef.current = false;
  }, [clientStyleData, emotionCache.sheet]);

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
        {serverStyleData?.map(({key, ids, css}) => (
          <style
            key={key}
            data-emotion={`${key} ${ids.join(" ")}`}
            /* eslint-disable security/noDangerouslySetInnerHtml */
            dangerouslySetInnerHTML={{__html: css}}
          />
        ))}
      </head>
      <body>
        <Global styles={globalStyles(fontSize, hasHydrated)} />
        <ThemeProvider
          theme={{
            ...theme,
            breakpoints: hasHydrated ? breakpoints : defaultBreakpoints,
          }}
        >
          {children}
        </ThemeProvider>
        <Scripts />
      </body>
    </html>
  );
});

export function ErrorBoundary() {
  const error = useRouteError();
  const data = useRouteLoaderData("root");
  if (isRouteErrorResponse(error)) {
    return (
      <FontSizeProvider>
        <NotFoundPage>
          <Page404 data={data} />
        </NotFoundPage>
      </FontSizeProvider>
    );
  }

  const errorMessage = error?.message || "Unknown error";

  // captureRemixErrorBoundaryError(error);

  return (
    <div>
      <h2>Something went wrong.</h2>
      <pre>{errorMessage}</pre>
    </div>
  );
}
